import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, List, ListItem, ListItemText, Button, Snackbar, Alert } from "@mui/material";
import axios from "axios";
import config from "../config.js";

const GetUserFiles = ({ userId, refreshTrigger }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const { API_BASE_URL } = config;

 

  const fetchFiles = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);

    const response = await axios.get(`${API_BASE_URL}/api/v1/files/${userId}`);
    const filesList = response.data.data?.files || [];
    setFiles(filesList);
  } catch (err) {
    console.error("Error fetching files:", err);
    setError("Failed to load files");
  } finally {
    setLoading(false);
  }
}, [userId,API_BASE_URL]);
 useEffect(() => {
    if (userId) {
      fetchFiles();
    }
  }, [userId, refreshTrigger,fetchFiles]);


 
  const downloadFile = async (fileId, fileName) => {
    setDownloadingFile(fileId);
    try {
     
      
      
      let downloadUrl;
      
      // Try this if your route is just /download/{fileId}
      downloadUrl = `${API_BASE_URL}/api/v1/download/${fileId}`;
      
     
      
      try {
        const response = await axios.get(downloadUrl, {
          responseType: 'blob'
        });
        
        // If successful, process the blob
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      } catch (err) {
        console.error("First download attempt failed:", err);
        // Continue to next attempt
      }
      
      // If the first attempt failed, try with the userId included
      try {
        downloadUrl = `${API_BASE_URL}/api/v1/download/${fileId}?userId=${userId}`;
        
        
        const response = await axios.get(downloadUrl, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      } catch (err) {
        console.error("Second download attempt failed:", err);
        // Continue to last attempt
      }
      
      // Last attempt - try using a POST request with fileId in body
      try {
        downloadUrl = `${API_BASE_URL}/api/v1/download`;
       
        
        const response = await axios.post(downloadUrl, 
          { fileId, userId },
          { responseType: 'blob' }
        );
        
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      } catch (err) {
        console.error("Third download attempt failed:", err);
        throw new Error("All download attempts failed");
      }
      
    } catch (err) {
      console.error("Error downloading file:", err);
      setSnackbar({
        open: true,
        message: "Failed to download file. Please check the console for details.",
        severity: "error",
      });
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter for Excel files
  const excelFiles = files.filter(file => 
    file.fileName.endsWith('.xlsx') || 
    file.fileName.endsWith('.xls')
  );

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      {loading && <CircularProgress size={20} />}
      
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      
      {!loading && !error && excelFiles.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No Excel files found
        </Typography>
      )}
      
      {!loading && !error && excelFiles.length > 0 && (
        <List dense sx={{ maxHeight: 150, overflowY: 'auto' }}>
          {excelFiles.map((file) => (
            <ListItem 
              key={file.fileId}
              sx={{ 
                borderRadius: 1,
                '&:hover': { bgcolor: '#f5f5f5' },
                mb: 0.5,
                border: '1px solid #e0e0e0',
                p: 1
              }}
            >
              <ListItemText
                primary={file.fileName}
                secondary={`${formatFileSize(file.size)} • ${formatDate(file.createdAt)}`}
                primaryTypographyProps={{ 
                  variant: 'body2',
                  sx: { fontWeight: 500, fontSize: '0.8rem' }
                }}
                secondaryTypographyProps={{ 
                  variant: 'caption',
                  sx: { fontSize: '0.7rem' }
                }}
              />
              <Button 
                size="small" 
                variant="outlined" 
                sx={{ ml: 1, minWidth: 'auto', p: '2px 8px', fontSize: '0.7rem' }}
                onClick={() => downloadFile(file.fileId, file.fileName)}
                disabled={downloadingFile === file.fileId}
              >
                {downloadingFile === file.fileId ? (
                  <CircularProgress size={14} />
                ) : (
                  "Get"
                )}
              </Button>
            </ListItem>
          ))}
        </List>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GetUserFiles;
